/* eslint-disable react-hooks/exhaustive-deps -- triggerToast is memoized and stable */
import i18n from "../../../i18n";
import { Trans } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Inbox, Plus, CalendarDays } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import {
  ContentCard,
  DataTable,
  EmptyState,
  LoadingState,
  PageHeader,
  ui
} from "../../../components/ui";
import { Button } from "../../../components/ui/button";
import { LeaveActionToast } from "../../leave-management/components";
import {
  CreateEventDialog,
  HolidayDeleteModal,
  HolidayEventRow,
  HolidayFilterBar
} from "../components";
import { INITIAL_FORM_STATE } from "../constants/formState";
import {
  createEventOnServer,
  updateEventOnServer,
  fetchHoliday,
  fetchEventsPage
} from "../../../services/holidayService";
import {
  fetchDepartments,
  fetchWorkLocations
} from "../../../services/holidayService";

export function AdminHolidaysPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, updateSearch] = useState("");
   const [eventPage, setEventPage] = useState(0);
   const [hasMoreEvents, setHasMoreEvents] = useState(false);
   const setSearch = (value) => { updateSearch(value); setEventPage(0); };

  const [activeSection, updateActiveSection] = useState("Holidays");
  const setActiveSection = (value) => { updateActiveSection(value); setEventPage(0); };
  const [typeFilter, setTypeFilter] = useState("All types");
  const [visibilityFilter, setVisibilityFilter] = useState("All visibility");
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [formState, setFormState] = useState(INITIAL_FORM_STATE);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showErrorSummary, setShowErrorSummary] = useState(false);
  const [deletingEvent, setDeletingEvent] = useState(null);
  const [toast, setToast] = useState(null);
  const [holidayTypes, setHolidayTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [scopeFilter, setScopeFilter] = useState("");

  const triggerToast = useCallback((message, type = "success") => {
    setToast({ message, type });
  }, []);

  useEffect(() => {
    if (!toast) return undefined;

    const timer = setTimeout(() => setToast(null), 4000);

    return () => clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    fetchHoliday()
      .then((data) =>
        setHolidayTypes(
          Array.isArray(data) ? data : data?.data ?? []
        )
      )
      .catch((error) => {
        setHolidayTypes([]);
        triggerToast(
          error.message || "Failed to load holiday types.",
          "error"
        );
      });
  }, []);

  useEffect(() => {
    if (
      visibilityFilter === "Department" &&
      departments.length === 0
    ) {
      fetchDepartments()
        .then((data) =>
          setDepartments(
            Array.isArray(data) ? data : data?.data ?? []
          )
        )
        .catch((error) => {
          setDepartments([]);
          triggerToast(
            error.message || "Failed to load department.",
            "error"
          );
        });
    }

    if (
      visibilityFilter === "Work Location" &&
      locations.length === 0
    ) {
      fetchWorkLocations()
        .then((data) =>
          setLocations(
            Array.isArray(data) ? data : data?.data ?? []
          )
        )
        .catch((error) => {
          setLocations([]);
          triggerToast(
            error.message || "Failed to load location.",
            "error"
          );
        });
    }
  }, [
    visibilityFilter,
    departments.length,
    locations.length
  ]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const loadEvents = async () => {
      setLoading(true);

      try {
        const result = await fetchEventsPage(search, { page: eventPage });
        const allEvents = result.events;
        if (!active) return;

        const sectionEvents =
          activeSection === "Holidays"
            ? allEvents.filter(
                (event) => event.type === "Public Holiday"
              )
            : allEvents.filter(
                (event) => event.type !== "Public Holiday"
              );

        setEvents((previous) => eventPage === 0 ? sectionEvents : [...previous, ...sectionEvents]);
        setHasMoreEvents(result.hasNext);
      } catch (error) {
        if (!active) return;
        setEvents([]);
        triggerToast(
          error.message ||
            "Failed to load holidays and events.",
          "error"
        );
      } finally {
        if (active) setLoading(false);
      }
    };

    const timer = setTimeout(loadEvents, 300);
    return () => { active = false; clearTimeout(timer); };
  }, [activeSection, search, eventPage]);

  const sectionTypeOptions = useMemo(
    () =>
      activeSection === "Holidays"
        ? holidayTypes.filter(
            (type) => type.categoryName === "Public Holiday"
          )
        : holidayTypes.filter(
            (type) => type.categoryName !== "Public Holiday"
          ),
    [holidayTypes, activeSection]
  );

  const filtered = useMemo(() => {
    return events
      .filter((e) => {
        if (new Date(`${e.start}T${e.startTime || "00:00"}`) < new Date()) return false;

        const matchType =
          typeFilter === "All types" ||
          e.type === typeFilter;

        const matchVisibility =
          visibilityFilter === "All visibility" ||
          e.visibility === visibilityFilter;

        return (
          matchType &&
          matchVisibility
        );
      })
      .sort(
        (a, b) =>
          new Date(a.start) -
          new Date(b.start)
      );
  }, [
    events,
    typeFilter,
    visibilityFilter
  ]);

  const validateField = (
    field,
    value,
    currentForm = formState
  ) => {
    let error = "";

    if (field === "title") {
      if (!value || !value.trim()) {
        error = "Title is required.";
      } else if (value.length > 80) {
        error = "Title cannot exceed 80 characters.";
      }
    }

    if (
      field === "description" &&
      value &&
      value.length > 500
    ) {
      error =
        "Description cannot exceed 500 characters.";
    }

    if (field === "start" && !value) {
      error = "Start date is required.";
    }

    if (field === "end") {
      if (!value) {
        error = "End date is required.";
      } else if (
        currentForm.start &&
        new Date(value) < new Date(currentForm.start)
      ) {
        error =
          "End must be on or after the start.";
      }
    }

    if (
      field === "scopeValues" &&
      currentForm.visibility !== "Global" &&
      (!value || value.length === 0)
    ) {
      error = `At least one ${currentForm.visibility.toLowerCase()} selection is required.`;
    }

    setFieldErrors((prev) => {
      const next = { ...prev };

      if (error) {
        next[field] = error;
      } else {
        delete next[field];
      }

      return next;
    });

    return error;
  };

  const runGlobalValidation = () => {
    const errors = {};

    if (!formState.title?.trim()) {
      errors.title = "Title is required.";
    }

    if (!formState.start) {
      errors.start = "Start date is required.";
    }

    if (!formState.end) {
      errors.end = "End date is required.";
    }

    if (
      formState.start &&
      formState.end &&
      new Date(formState.end) <
        new Date(formState.start)
    ) {
      errors.end =
        "End must be on or after the start.";
    }

    if (
      formState.visibility !== "Global" &&
      formState.scopeValues.length === 0
    ) {
      errors.scopeValues =
        "At least one selection is required.";
    }

    setFieldErrors(errors);

    return errors;
  };

  const isFormValid = useMemo(() => {
    if (
      !formState.title?.trim() ||
      !formState.start ||
      !formState.end
    ) {
      return false;
    }

    if (
      new Date(formState.end) <
      new Date(formState.start)
    ) {
      return false;
    }

    if (
      formState.visibility !== "Global" &&
      formState.scopeValues.length === 0
    ) {
      return false;
    }

    return true;
  }, [formState]);

  const handleSaveSubmit = async (e) => {
    e.preventDefault();

    const runtimeErrors =
      runGlobalValidation();

    if (Object.keys(runtimeErrors).length > 0) {
      setShowErrorSummary(true);
      return;
    }

    if (formState.id) {
      try {
        await updateEventOnServer(formState);

        setEvents((prev) =>
          prev.map((evt) =>
            evt.id === formState.id
              ? {
                  ...formState,
                  title:
                    formState.title.trim()
                }
              : evt
          )
        );

        triggerToast(
          `Event "${formState.title.trim()}" successfully updated.`
        );
      } catch (error) {
        triggerToast(
          error.message ||
            "Failed to update. Please try again.",
          "error"
        );
        return;
      }
    } else {
      try {
        const serverResponse =
          await createEventOnServer(formState);

        const newEvent = {
          ...formState,
          id: serverResponse.data.eventId,
          title: formState.title.trim()
        };

        setEvents((prev) => [
          ...prev,
          newEvent
        ]);

        triggerToast(
          `Event "${formState.title.trim()}" successfully published.`
        );
      } catch (error) {
        triggerToast(
          error.message ||
            "Failed to save. Please try again.",
          "error"
        );
        return;
      }
    }

    setIsPanelOpen(false);
    setFormState(INITIAL_FORM_STATE);
    setShowErrorSummary(false);
  };

  const executeDeletion = async () => {
    try {
      await updateEventOnServer({
        ...deletingEvent,
        isEnabled: false
      });

      setEvents((prev) =>
        prev.filter(
          (evt) =>
            evt.id !== deletingEvent.id
        )
      );

      triggerToast(
        `Event "${deletingEvent.title}" removed completely.`
      );
    } catch (error) {
      triggerToast(
        error.message ||
          "Failed to delete. Please try again.",
        "error"
      );
    }

    setDeletingEvent(null);
  };

  const openCreatePanel = () => {
    setFormState(INITIAL_FORM_STATE);
    setFieldErrors({});
    setShowErrorSummary(false);
    setIsPanelOpen(true);
  };

  const openEditPanel = (event) => {
    setFormState(event);
    setFieldErrors({});
    setShowErrorSummary(false);
    setIsPanelOpen(true);
  };

  return (
    <AppLayout
      overlay={
        <>
          <LeaveActionToast toast={toast} />

          <CreateEventDialog
            isPanelOpen={isPanelOpen}
            setIsPanelOpen={setIsPanelOpen}
            formState={formState}
            setFormState={setFormState}
            fieldErrors={fieldErrors}
            setFieldErrors={setFieldErrors}
            showErrorSummary={
              showErrorSummary
            }
            validateField={validateField}
            handleSaveSubmit={
              handleSaveSubmit
            }
            isFormValid={isFormValid}
            triggerToast={triggerToast}
          />

          <HolidayDeleteModal
            deletingEvent={deletingEvent}
            setDeletingEvent={
              setDeletingEvent
            }
            executeDeletion={
              executeDeletion
            }
          />
        </>
      }
    >

      <PageHeader back={<PageBackLink to="/" />}
        title={i18n.t(
          "dateTime.holidaysEvents"
        )}
        action={
          <Button
            type="button"
            onClick={openCreatePanel}
            className={ui.primaryBtn}
          >
            <Plus className="h-4 w-4" />
            <Trans i18nKey="formActions.createEvent" />
          </Button>
        }
      />

      <div className="mt-4 border-b border-(--color-text-border)">
        <div className="flex items-center gap-8 px-1">
          <button
            type="button"
            onClick={() => {
              setActiveSection("Holidays");
              setTypeFilter("All types");
            }}
            className={`relative pb-3 text-sm font-semibold transition-colors ${
              activeSection === "Holidays"
                ? "text-primary"
                : "per-file-hint hover:text-foreground"
            }`}
          >
            Holidays

            {activeSection ===
              "Holidays" && (
              <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-primary" />
            )}
          </button>

          <button
            type="button"
            onClick={() => {
              setActiveSection("Events");
              setTypeFilter("All types");
            }}
            className={`relative pb-3 text-sm font-semibold transition-colors ${
              activeSection === "Events"
                ? "text-primary"
                : "per-file-hint hover:text-foreground"
            }`}
          >
            Events

            {activeSection ===
              "Events" && (
              <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-primary" />
            )}
          </button>
        </div>
      </div>

      <HolidayFilterBar
        search={search}
        setSearch={setSearch}
        typeFilter={typeFilter}
        setTypeFilter={setTypeFilter}
        visibilityFilter={
          visibilityFilter
        }
        setVisibilityFilter={
          setVisibilityFilter
        }
        typeOptions={sectionTypeOptions}
        departments={departments}
        locations={locations}
        scopeFilter={scopeFilter}
        setScopeFilter={setScopeFilter}
      />

      <ContentCard className="mt-4">
        {loading ? (
          <LoadingState message="Loading holidays & events…" />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={CalendarDays}
            title={
              activeSection === "Holidays"
                ? i18n.t("dateTime.noUpcomingHolidays")
                : i18n.t("dateTime.noUpcomingEvents")
            }
            message={
              activeSection === "Holidays"
                ? "No upcoming holidays for you."
                : "No upcoming events for you."
            } />
        ) : (
          <DataTable paginate
            minWidth="860px"
            className="block"
            columns={[
              {
                key: "title",
                label: "Title"
              },
              {
                key: "type",
                label: "Type"
              },
              {
                key: "start",
                label: "Start",
                className:
                  "whitespace-nowrap"
              },
              {
                key: "end",
                label: "End",
                className:
                  "whitespace-nowrap"
              },
              {
                key: "allDay",
                label: "All-day",
                className:
                  "whitespace-nowrap"
              },
              {
                key: "visibility",
                label: "Visibility"
              },
              {
                key: "actions",
                label: "Actions"
              }
            ]}
          >
            {filtered.map((event) => (
              <HolidayEventRow
                key={event.id}
                event={event}
                openEditPanel={
                  openEditPanel
                }
                setDeletingEvent={
                  setDeletingEvent
                }
              />
            ))}
          </DataTable>
        )}
      </ContentCard>
       {hasMoreEvents && (
         <button type="button" className="btn-secondary" disabled={loading}
           onClick={() => setEventPage((current) => current + 1)}>
           {loading ? "Loading..." : "Load more"}
         </button>
       )}
    </AppLayout>
  );
}
